import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { borrowerDetailQueryOptions } from "@/lib/targets";
import { idFromSlug } from "@/lib/targets-schemas";
import { TargetBadge } from "@/components/targets/TargetBadge";
import { TargetStatusControl } from "@/components/targets/TargetStatusControl";
import { StatusPill } from "@/components/loans/StatusPill";
import { formatUsd, formatLtvBps, formatPhoneUs, formatRelativeTime } from "@/lib/format";
import { radarBorrowerUrl } from "@/lib/radar";
import { ExternalLinkIcon } from "@/components/ExternalLinkIcon";
import { apiFetch } from "@/lib/api-client";
import { useToast } from "@/components/ui/Toast";
import { useTopbar } from "@/components/chrome/use-topbar";
import { ManualContactsSection } from "@/components/contacts/ManualContactsSection";

export const Route = createFileRoute("/_app/targets/$key/")({
  component: BorrowerDetailPage,
});

function BorrowerDetailPage() {
  const { key } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading, error } = useQuery(borrowerDetailQueryOptions(key));

  const refresh = useMutation({
    mutationFn: () =>
      apiFetch(`/api/borrowers/${encodeURIComponent(idFromSlug(key))}/refresh-history`, {
        method: "POST",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["targets"] });
      toast.success("History refreshed");
    },
    onError: () => toast.error("Could not refresh history"),
  });

  const { setTopbar } = useTopbar();
  const name = data?.displayName ?? "";
  useEffect(() => {
    setTopbar({
      crumbs: [
        { label: "Operations" },
        { label: "Targets", to: "/targets" },
        { label: name || key },
      ],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar, name, key]);

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (error || !data) {
    return (
      <div className="rounded-lg border border-rule bg-surface px-8 py-10 text-center">
        <h2 className="mb-2 text-lg font-semibold text-ink">Borrower not found</h2>
        <Link to="/targets" className="text-sm font-semibold text-ink hover:underline">
          ← Back to targets
        </Link>
      </div>
    );
  }

  const firstState = data.states[0] ?? null;
  const discovered = data.loans.filter((l) => l.source === "discovery");
  const pulled = data.loans.filter((l) => l.source === "borrower_pull");

  return (
    <div className="space-y-5 md:space-y-7">
      <article className="rounded-lg border border-rule bg-surface px-6 py-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <a
              href={radarBorrowerUrl(data.displayName, firstState)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[20px] font-semibold tracking-tight text-ink hover:underline"
            >
              {data.displayName}
              <ExternalLinkIcon />
            </a>
            <div className="mt-1 flex items-center gap-2 text-xs text-muted">
              Principal: {data.principalName ?? "—"}
              <TargetBadge status={data.pursuitStatus} />
            </div>
          </div>
          <TargetStatusControl
            borrowerId={data.id}
            displayName={data.displayName}
            currentStatus={data.pursuitStatus}
          />
        </div>
      </article>

      <section className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-7">
        <article className="rounded-lg border border-rule bg-surface px-6 py-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted">
              Contact &amp; roll-up
            </h3>
            <span className="rounded-full border border-rule bg-paper-2 px-2 py-0.5 text-[8.5px] font-bold uppercase tracking-wide text-muted">
              System · PLR
            </span>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
            <dt className="text-muted">Phone</dt>
            <dd className="text-ink">{formatPhoneUs(data.primaryPhone) ?? "—"}</dd>
            <dt className="text-muted">Email</dt>
            <dd className="truncate text-ink">{data.primaryEmail ?? "—"}</dd>
            <dt className="text-muted">Discovered loans</dt>
            <dd className="tabular-nums text-ink">{data.loanCount}</dd>
            <dt className="text-muted">Total Δ</dt>
            <dd className="font-semibold tabular-nums text-pos">
              {data.totalDeltaUsd > 0
                ? `+${formatUsd(data.totalDeltaUsd)}`
                : formatUsd(data.totalDeltaUsd)}
            </dd>
            <dt className="text-muted">Loan volume</dt>
            <dd className="tabular-nums text-ink">{formatUsd(data.totalLoanUsd)}</dd>
            <dt className="text-muted">Last seen</dt>
            <dd className="text-ink">
              {data.lastSeenAt ? formatRelativeTime(data.lastSeenAt) : "—"}
            </dd>
          </dl>
        </article>
        <article className="rounded-lg border border-rule bg-surface px-6 py-5">
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted">
            Other info
          </h3>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-[13px]">
            <dt className="text-muted">Markets / states</dt>
            <dd className="text-ink">{data.states.join(", ") || "—"}</dd>
            <dt className="text-muted">Lenders used</dt>
            <dd className="text-ink">{data.lendersUsed.join(", ") || "—"}</dd>
            <dt className="text-muted">Prior loans</dt>
            <dd className="tabular-nums text-ink">{data.maxPriorLoansCount}</dd>
            <dt className="text-muted">Negative remarks</dt>
            <dd className={data.hasNegativeRemarks ? "font-semibold text-neg" : "text-pos"}>
              {data.hasNegativeRemarks ? "On file" : "None"}
            </dd>
          </dl>
        </article>
      </section>

      <article className="rounded-lg border border-rule bg-surface px-6 py-5">
        <ManualContactsSection borrowerId={data.id} contacts={data.manualContacts} mode="full" />
      </article>

      <article className="rounded-lg border border-rule bg-surface px-6 py-5">
        <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted">
          Discovered loans ({discovered.length})
        </h3>
        <ul className="flex flex-col divide-y divide-rule">
          {discovered.map((l) => (
            <li key={l.id} className="py-3">
              <Link
                to="/loans/$id"
                params={{ id: l.id }}
                className="flex flex-col gap-1 hover:opacity-80 md:flex-row md:items-center md:justify-between"
              >
                <div className="min-w-0">
                  <span className="block font-medium text-ink">{l.street}</span>
                  <span className="block text-[12px] text-muted-2">
                    {l.city}, {l.state}
                    {l.zip ? ` ${l.zip}` : ""} · {l.lender}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-[13px]">
                  <span className="text-muted">Rate {formatLtvBps(l.lastSaleRateBps)}</span>
                  <span className="font-semibold tabular-nums text-pos">
                    {l.deltaUsd != null && l.deltaUsd > 0
                      ? `+${formatUsd(l.deltaUsd)}`
                      : formatUsd(l.deltaUsd)}
                  </span>
                  <StatusPill status={l.status} />
                </div>
              </Link>
            </li>
          ))}
          {discovered.length === 0 && (
            <li className="py-3 text-[13px] text-muted">No discovered loans.</li>
          )}
        </ul>
      </article>

      <article className="rounded-lg border border-rule bg-surface px-6 py-5">
        <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted">
            PLR loan history ({pulled.length})
          </h3>
          <div className="flex items-center gap-3 text-[12px] text-muted">
            <span>
              {data.historyPulledAt
                ? `Pulled ${formatRelativeTime(data.historyPulledAt)}`
                : "Not pulled yet"}
            </span>
            <button
              type="button"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
              className="min-h-[44px] rounded-md border border-rule bg-surface px-3 py-2 text-xs font-semibold text-ink hover:border-gold disabled:opacity-60 md:min-h-0 md:py-1.5"
            >
              {refresh.isPending ? "Refreshing…" : "Refresh history"}
            </button>
          </div>
        </div>
        <ul className="flex flex-col divide-y divide-rule">
          {pulled.map((l) => (
            <li
              key={l.id}
              className="flex flex-col gap-1 py-3 md:flex-row md:items-center md:justify-between"
            >
              <div className="min-w-0">
                <span className="block font-medium text-ink">{l.street}</span>
                <span className="block text-[12px] text-muted-2">
                  {l.city}, {l.state}
                  {l.zip ? ` ${l.zip}` : ""} · {l.lender}
                </span>
              </div>
              <div className="flex items-center gap-4 text-[13px] text-muted">
                <span>{formatUsd(l.loanAmountUsd)}</span>
                <span>{l.recordingDate ? formatRelativeTime(l.recordingDate) : "—"}</span>
              </div>
            </li>
          ))}
          {pulled.length === 0 && (
            <li className="py-3 text-[13px] text-muted">
              No PLR history pulled yet. Add this borrower as a target (or hit Refresh) to pull it.
            </li>
          )}
        </ul>
      </article>
    </div>
  );
}
